import {
  DEFAULT_SPEC_ANALYTICS,
  SpaceAnalyticsLocation,
  SpecAnalytics,
  trackSpaceAddCard,
  trackSpaceAddObject,
  trackSpaceSaveObject,
  trackSpaceTransferObject,
  trackUserBaseSelector,
} from '@fullstory/analytics';
import { intl } from '@fullstory/intl';
import {
  CreateSectionCardRequest,
  HomeCard,
  ObjectType,
  Space,
} from '@fullstory/proto/fs/services/dx-objects';
import { useNotifications } from '@fullstory/react-hooks';
import {
  AppDispatch,
  fullstorySelectors,
  SpaceRequestActions,
  useAllSavedFunnels,
  useAllSavedMetrics,
} from '@fullstory/redux';
import { generateRoutePath, SPACE_ROUTE } from '@fullstory/routes';
import { useCallback } from 'react';
import { defineMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  DEFAULT_CARD_FORMAT,
  DEFAULT_DASHBOARD_CHART_TYPE,
  DEFAULT_FUNNEL_CHART_TYPE,
  DEFAULT_HEATMAP_CARD_FORMAT,
  DEFAULT_HEATMAP_CHART_TYPE,
  DEFAULT_RETENTION_CHART_TYPE,
  DEFAULT_SEGMENT_CARD_FORMAT,
  DEFAULT_SEGMENT_CHART_TYPE,
} from '../../constants';
import { SpaceActionType } from '../../types';
import {
  CardEmbeddedObject,
  getDefaultCardSizeForMetric,
  getDefaultChartTypeForFunnel,
  getDefaultChartTypeForMetric,
  toSpaceCardAnalytics,
  toSpaceObjectAnalytics,
  toSpaceTransferObjectAnalytics,
} from '../../utils';
import { UNTITLED_SECTION_ID, UNTITLED_SECTION_LABEL } from '../../hooks';

const successMessages: {
  [M in Exclude<SpaceActionType, 'save'>]: {
    defaultMessage: string;
    id: string;
    description: string;
  };
} = {
  add: defineMessage({
    defaultMessage:
      '{objectType, select, 1 {Metric} 2 {Funnel} 3 {Retention} 5 {Segment} 6 {Dashboard} 7 {heatmap} 8 {journey} other {Unknown type}} successfully added to {spaceName}.',
    id: 'oNzdD8',
    description: 'Notification that an object was successfully added to a space',
  }),
  feature: defineMessage({
    defaultMessage:
      '{objectType, select, 1 {Metric} 2 {Funnel} 3 {Retention} 5 {Segment} 6 {Dashboard} 7 {heatmap} 8 {journey} other {Unknown type}} successfully featured on {spaceName}.',
    id: 'yfI0U3',
    description: 'Notification that an object was successfully featured on a space',
  }),
  transfer: defineMessage({
    defaultMessage:
      '{objectType, select, 1 {Metric} 2 {Funnel} 3 {Retention} 5 {Segment} 6 {Dashboard} 7 {heatmap} 8 {journey} other {Unknown type}} successfully transferred to {spaceName}.',
    id: 'TO9uYS',
    description: 'Notification that an object was successfully transferred to a space',
  }),
};

const errorMessages: {
  [M in Exclude<SpaceActionType, 'save'>]: {
    defaultMessage: string;
    id: string;
    description: string;
  };
} = {
  add: defineMessage({
    defaultMessage:
      'Unable to add {objectType, select, 1 {metric} 2 {funnel} 3 {retention} 5 {segment} 6 {dashboard} 7 {heatmap} 8 {journey} other {unknown type}} to {spaceName}.',
    id: 'zg3i7n',
    description: 'Notification that an object was successfully added to a space',
  }),
  feature: defineMessage({
    defaultMessage:
      'Unable to feature {objectType, select, 1 {metric} 2 {funnel} 3 {retention} 5 {segment} 6 {dashboard} 7 {heatmap} 8 {journey} other {unknown type}} on {spaceName}.',
    id: 'lhpA4J',
    description: 'Notification that an object was successfully featured on a space',
  }),
  transfer: defineMessage({
    defaultMessage:
      'Unable to transfer {objectType, select, 1 {metric} 2 {funnel} 3 {retention} 5 {segment} 6 {dashboard} 7 {heatmap} 8 {journey} other {unknown type}} to {spaceName}.',
    id: 'hv+20Y',
    description: 'Notification that an object was successfully transferred to a space',
  }),
};

export const createFeatureCard = (
  dxObjectId: string,
  dxObjectType: ObjectType,
  chartType?: number,
  format?: number,
): Omit<CreateSectionCardRequest, 'orgId' | 'sectionId'> => {
  const card = {
    dxObjectId,
    dxObjectType,
    funnelMetricType: HomeCard.FunnelMetricType.FUNNEL_METRIC_TYPE_INVALID as number,
    format: format ?? DEFAULT_CARD_FORMAT,
    chartType: chartType ?? HomeCard.ChartType.CHART_TYPE_INVALID,
    markdown: '',
  };

  switch (dxObjectType) {
    case ObjectType.FUNNEL:
      card.chartType = chartType ? chartType : DEFAULT_FUNNEL_CHART_TYPE;
      break;
    case ObjectType.RETENTION:
      card.chartType = chartType ? chartType : DEFAULT_RETENTION_CHART_TYPE;
      break;
    case ObjectType.SEGMENT:
      card.format = format ? format : DEFAULT_SEGMENT_CARD_FORMAT;
      card.chartType = chartType ? chartType : DEFAULT_SEGMENT_CHART_TYPE;
      break;
    case ObjectType.DASHBOARD:
      card.chartType = DEFAULT_DASHBOARD_CHART_TYPE;
      break;
    case ObjectType.HEATMAP:
      card.format = DEFAULT_HEATMAP_CARD_FORMAT;
      card.chartType = DEFAULT_HEATMAP_CHART_TYPE;
      break;
    case ObjectType.METRIC:
      break;
    default:
      break;
  }

  return card;
};

type CreateSectionDescriptor = { name: string; index: number };

export const useSaveSpaceObjectModal = (location: SpaceAnalyticsLocation) => {
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const { byId: metricsById } = useAllSavedMetrics();
  const { byId: funnelsById } = useAllSavedFunnels();
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useNotifications();
  const userAnalytics = useSelector(trackUserBaseSelector);

  const onSave = useCallback(
    async ({
      mode,
      objectId,
      objectType,
      space,
      keepReference,
      section,
      specAnalytics = DEFAULT_SPEC_ANALYTICS,
      sourceSpaceId,
      chartType,
      format,
      cardEventObject,
    }: {
      mode: SpaceActionType;
      objectId: string;
      objectType: ObjectType;
      space: Space;
      keepReference: boolean;
      // the string for an existing section id, CreateSectionDescriptor is for creating a new section to feature into
      section?: string | CreateSectionDescriptor;
      specAnalytics?: SpecAnalytics;
      sourceSpaceId?: string;
      chartType?: number;
      format?: number;
      cardEventObject?: CardEmbeddedObject;
    }) => {
      const payload = {
        orgId,
        dxObjectId: objectId,
        dxObjectType: objectType,
        spaceId: space.id,
      };

      try {
        if (mode === 'transfer' || mode === 'save') {
          await dispatch(SpaceRequestActions.moveObjectToSpace({ ...payload, keepReference }));
        } else {
          // add or feature
          await dispatch(SpaceRequestActions.linkObjectToSpace(payload));
        }

        let resolvedSectionId = typeof section === 'string' ? section : '';

        if (section) {
          try {
            let resolvedFormat = format;
            let resolvedChartType = chartType;
            // only overwrite if its not provided
            if (
              objectType === ObjectType.METRIC &&
              metricsById[objectId] &&
              format === undefined &&
              chartType === undefined
            ) {
              resolvedChartType = getDefaultChartTypeForMetric(
                metricsById[objectId]!.metric?.metric,
              );
              resolvedFormat = getDefaultCardSizeForMetric(metricsById[objectId]!.metric?.metric);
            } else if (
              objectType === ObjectType.FUNNEL &&
              funnelsById[objectId] &&
              chartType === undefined
            ) {
              resolvedChartType = getDefaultChartTypeForFunnel(
                funnelsById[objectId]!.funnel?.funnel?.funnelSettings?.timeInterval,
              );
            }

            // if the section is the UNTITLED_SECTION_ID, we need to create a new section
            const sectionOrCreate: string | CreateSectionDescriptor =
              typeof section === 'string' && section === UNTITLED_SECTION_ID
                ? { name: UNTITLED_SECTION_LABEL, index: 0 }
                : section;
            if (typeof sectionOrCreate !== 'string') {
              const sectionResponse = await dispatch(
                SpaceRequestActions.createSection({
                  orgId,
                  name: sectionOrCreate.name,
                  spaceId: space.id,
                  index: sectionOrCreate.index,
                }),
              );
              // should always have a section id here
              resolvedSectionId = sectionResponse.section?.id ?? '';
            }

            if (resolvedSectionId) {
              const featureCard = createFeatureCard(
                objectId,
                objectType,
                resolvedChartType,
                resolvedFormat,
              );
              const rsp = await dispatch(
                SpaceRequestActions.createCard({
                  orgId,
                  sectionId: resolvedSectionId,
                  ...featureCard,
                }),
              );
              if (mode !== 'save') {
                notifications.successLink(
                  intl.formatMessage(successMessages[mode], { objectType, spaceName: space.name }),
                  intl.formatMessage({ defaultMessage: 'See it now', id: 'BAgGJC' }),
                  // if there is no featured section selected, send to the Library tab
                  generateRoutePath(
                    SPACE_ROUTE,
                    { orgId, id: space.id },
                    { tab: resolvedSectionId ? 'home' : 'library' },
                  ),
                );
              }

              if (mode === 'feature') {
                if (rsp.card) {
                  trackSpaceAddCard({
                    ...userAnalytics,
                    ...specAnalytics,
                    ...toSpaceCardAnalytics(rsp.card, space.id, resolvedSectionId),
                    space_id_str: space.id,
                    location_str: location,
                  });
                }
              }
            }
          } catch (err: any) {
            if (err.status === 412) {
              // Precondition failed status means we are at the max card limit
              notifications.error(
                intl.formatMessage({
                  defaultMessage: 'This space already has the maximum number of cards allowed',
                  id: '3xYgzM',
                }),
              );
            } else {
              notifications.error(
                intl.formatMessage({
                  defaultMessage: 'Unable to create feature card',
                  id: 'gzMiWp',
                }),
              );
            }
          }
        }

        if (mode === 'transfer') {
          trackSpaceTransferObject({
            ...userAnalytics,
            ...toSpaceTransferObjectAnalytics(
              sourceSpaceId ?? '',
              space.id,
              objectId,
              objectType,
              keepReference,
              section ? resolvedSectionId : undefined,
              location,
            ),
          });
        } else if (mode === 'add' || mode === 'save') {
          const trackAction = mode === 'add' ? trackSpaceAddObject : trackSpaceSaveObject;
          trackAction({
            ...userAnalytics,
            ...toSpaceObjectAnalytics(
              space.id,
              objectId,
              objectType,
              section ? resolvedSectionId : undefined,
              location,
              cardEventObject,
            ),
          });
        }
      } catch (e) {
        console.error(`Could not ${mode} object to space`, space.id, objectId, objectType);
        if (mode !== 'save') {
          notifications.error(
            intl.formatMessage(errorMessages[mode], {
              mode,
              objectType,
              spaceName: space.name,
            }),
          );
        }
        throw e;
      }
    },
    [dispatch, funnelsById, location, metricsById, notifications, orgId, userAnalytics],
  );

  return onSave;
};

export const useSaveSpaceObjectModalFromDetailPage = () =>
  useSaveSpaceObjectModal('object detail page');
